import { Navigate, Outlet } from 'react-router-dom';
import { usePermissions } from 'contexts/PermissionsContext';
import { PermissionFlag } from 'contexts/PermissionsContext';

interface PermissionGuardProps {
  permission: PermissionFlag;
  /** Element to render when permission is granted. Defaults to <Outlet /> for nested routes. */
  children?: React.ReactNode;
}

/**
 * Renders children (or <Outlet />) when the user holds the given permission.
 * Redirects to /maintenance/404 otherwise.
 *
 * hasPermission() returns true while loading to avoid premature redirects.
 */
export default function PermissionGuard({ permission, children }: PermissionGuardProps) {
  const { hasPermission } = usePermissions();

  if (!hasPermission(permission)) {
    return <Navigate to="/maintenance/404" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

/**
 * Only allows access when the user has no community role memberships
 * (i.e. permissions === null — HOA admin).
 * Community role members are redirected to /maintenance/404.
 *
 * Use this for routes whose permission flags are not yet exposed by the
 * myRolesInCommunity GraphQL resolver (e.g. canManageRoles, canManageBilling).
 */
export function HOAOnlyGuard({ children }: { children?: React.ReactNode }) {
  const { permissions, isLoading } = usePermissions();

  // While loading, allow through to avoid premature redirects.
  if (isLoading) return children ? <>{children}</> : <Outlet />;

  if (permissions !== null) {
    return <Navigate to="/maintenance/404" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
